import { buildEndPoint } from '@mairistem/tools';
import _ from 'lodash';
import { ETATS_FACTURE } from '@/constants';
import { formatFilters, formatSort } from '@/utils';

import baseUrl from './base';

export const getFacture = (id: number) => `${baseUrl}/factures/${id}`;
export const getFactures = (
  { page, perPage, sorts, filters } = {
    page: 1,
    perPage: 30,
    sorts: [],
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures`, {
    page,
    perPage: perPage || 30,
    sort: formatSort(sorts),
    ...formatFilters(filters),
    prelevee: 'rejetee',
  });

export const getFacturesPrelevees = (
  { page, perPage, filters } = {
    page: 1,
    perPage: 1000, // pour la MVP
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures`, {
    page,
    perPage: perPage || 1000, // pour la MVP
    ...formatFilters(filters),
    sort: 'cycleFacturation,numero:desc',
    prelevee: true,
    rejet: false,
    etat: `in(${ETATS_FACTURE.A_ENCAISSER},${ETATS_FACTURE.ENCAISSEMENT_PARTIEL})`,
  });

export const getFacturesRejets = (
  { page, perPage, filters } = {
    page: 1,
    perPage: 30,
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures`, {
    page,
    perPage: perPage || 30,
    ...formatFilters(filters),
    sort: 'cycleFacturation,dateRejet:desc',
    prelevee: true,
    rejet: true,
    full: true,
  });

export const getFacturesImpayees =
  (
    { cycles, typeTransfert, seuil } = {
      cycles: [],
      typeTransfert: 'facture',
      seuil: 0,
    },
  ) =>
  (
    { page, perPage } = {
      page: 1,
      perPage: 15,
    },
  ) =>
    buildEndPoint(`${baseUrl}/factures`, {
      page,
      perPage: perPage || 15,
      sort: 'cycleFacturation,numero:desc',
      prelevee: 'rejetee',
      identifiantCycle: `in(${_.join(cycles, ',')})`,
      ...(typeTransfert === 'payeur'
        ? { montantAEncaisserPayeur: `greaterOrEqual(${seuil})` }
        : { montantAEncaisser: `greaterOrEqual(${seuil})` }),
    });

// `avantEcheance` : relances avant date limite de paiement, dont l'envoi par mail suit le flag
// `envoiRelanceAvantEcheanceParMail` de la régie (et non `envoiRelanceParMail`).
export const getFacturesResteAPayer = (
  { cycles, typeTransfert, seuil, avantEcheance } = {
    cycles: [],
    typeTransfert: 'facture',
    seuil: 0,
  } as { cycles: number[]; typeTransfert: string; seuil: number; avantEcheance?: boolean },
) =>
  buildEndPoint(`${baseUrl}/factures/reste-a-payer`, {
    prelevee: 'rejetee',
    identifiantCycle: `in(${_.join(cycles, ',')})`,
    ...(typeTransfert === 'payeur'
      ? { montantAEncaisserPayeur: `greaterOrEqual(${seuil})` }
      : { montantAEncaisser: `greaterOrEqual(${seuil})` }),
    ...(avantEcheance ? { avantEcheance: true } : {}),
  });

export const getFacturesFull = (
  { page, perPage, sorts, filters } = {
    page: 1,
    perPage: 30,
    sorts: [],
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures`, {
    page,
    perPage: perPage || 30,
    sort: formatSort(sorts),
    ...formatFilters(filters),
    full: true,
  });

export const searchFactures = (
  { page, query, perPage, sorts, filters } = {
    page: 1,
    query: '',
    perPage: 30,
    sorts: [],
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures/search`, {
    page,
    perPage: perPage || 30,
    q: query,
    sort: formatSort(sorts),
    ...formatFilters(filters),
    prelevee: 'rejetee',
  });

export const searchFacturesPrelevees = (
  { page, query, perPage, filters } = {
    page: 1,
    query: '',
    perPage: 1000, // pour la MVP
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures/search`, {
    page,
    perPage: perPage || 1000, // pour la MVP
    q: query,
    sort: 'cycleFacturation,numero:desc',
    ...formatFilters(filters),
    prelevee: true,
    rejet: false,
    etat: `in(${ETATS_FACTURE.A_ENCAISSER},${ETATS_FACTURE.ENCAISSEMENT_PARTIEL})`,
  });

export const searchFacturesRejets = (
  { page, query, perPage, filters } = {
    page: 1,
    query: '',
    perPage: 30,
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures/search`, {
    page,
    perPage: perPage || 30,
    q: query,
    sort: 'cycleFacturation,dateRejet:desc',
    ...formatFilters(filters),
    prelevee: true,
    rejet: true,
  });

export const searchFacturesFull = (
  { page, query, perPage, sorts, filters } = {
    page: 1,
    query: '',
    perPage: 30,
    sorts: [],
    filters: [],
  },
) =>
  buildEndPoint(`${baseUrl}/factures/search`, {
    page,
    perPage: perPage || 30,
    q: query,
    sort: formatSort(sorts),
    ...formatFilters(filters),
  });

export const putFacture = (id) => `${baseUrl}/factures/${id}`;
// Contrôle de l'état de paiement MFO d'un lot de factures, avant encaissement au guichet.
export const postFacturesEtatMfo = () => `${baseUrl}/factures/etat-mfo`;
export const postPrelevementsRejet = () => `${baseUrl}/prelevements/rejeter`;
export const postPrelevementsValider = () => `${baseUrl}/prelevements/valider`;
